import React, { ReactChild } from 'react'
import { Pecado, pecados_base } from '../data/pecados'

import { shuffle } from '@elgatoylacaja/utils'

type User = {
  accessToken: string
  experimentDone: boolean
  allowsNews?: boolean
  prevAllowsNews: boolean | null
}

export type AppContextData = {
  datetime: string
  auth: boolean
  visited: Array<string>
  user?: User
  experiment: Experiment
  regressors?: Regressors
}

export type Parte1Data = {
  values: Array<{
    pecado: number // id de pecado
    presente_inicial: number
    presente: number
    frecuencia: number // value de frecuencia
    intensidad: number
  }>
  time: number
}

export type Parte2Data = {
  values: Array<{
    pecado: number
    ubicacion_inicial: number
    ubicacion: number
  }>
  time: number
}

export type Parte3Data = {
  values: Array<{
    pecado: number
    propio: number
    ajeno: number
  }>
  time: number
}

export type Parte4Data = {
  values: {
    orden_final: Array<number> // lista de ids de pecados
  }
  time: number
}

export type Parte5Data = {
  values: Array<{
    pecado: number // id de pecado
    posicion_final: [number, number] // (x, y) del pecado
  }>
  time: number
}

export type Experiment = {
  fork: 1 | 2
  pecados: Array<Pecado>
  parte_1?: Parte1Data
  parte_2?: Parte2Data
  parte_3?: Parte3Data
  parte_4?: Parte4Data
  parte_5?: Parte5Data
}

type Regressors = {
  jugador: string
  edad: number | string
  genero: string
  hincha: string
  movimiento: string
  identificacion_pais: number
  nivel_educativo: string
  importancia_religion: number
  criarion_bajo_religion: string
  pertenencia_religion: string
  creencia: string
  vida_despues_de_la_muerte: string
  porcentaje_menor: string | number
}

const initialAppContextData: AppContextData = {
  datetime: new Date().toLocaleString('es', { hour12: false }),
  auth: false,
  visited: [],
  experiment: {
    fork: 1,
    pecados: [],
  },
}

type Omit<T, K extends string | number | symbol> = {
  [P in Exclude<keyof T, K>]: T[P]
}

type Login = {
  type: 'LOGIN'
  user: Omit<User, 'allowsNews'>
}

type SaveRegressors = {
  type: 'SAVE_REGRESSORS'
  regressors: Regressors
}

type Initialize = {
  type: 'INITIALIZE'
}

type SetFork = {
  type: 'SET_FORK'
  fork: 1 | 2
}

type Parte = Parte1Data | Parte2Data | Parte3Data | Parte4Data | Parte5Data

type Save<T extends Parte> = {
  type: 'SAVE_PARTE'
  parte: Exclude<keyof Experiment, 'pecados' | 'fork'>
  values: T['values']
  time: number
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
  allowsNews: boolean
}

type ReducerAction =
  | AcceptTerms
  | Login
  | SaveRegressors
  | Initialize
  | SetFork
  | Save<Parte1Data>
  | Save<Parte2Data>
  | Save<Parte3Data>
  | Save<Parte4Data>
  | Save<Parte5Data>
  | Visit

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'LOGIN': {
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          ...action.user,
        },
      }
    }
    case 'SAVE_REGRESSORS': {
      return {
        ...state,
        regressors: action.regressors,
      }
    }
    case 'INITIALIZE': {
      return {
        ...state,
        experiment: {
          fork: [1, 2][new Date().getTime() % 2] as 1 | 2,
          pecados: shuffle(pecados_base),
        },
      }
    }
    case 'SET_FORK': {
      return {
        ...state,
        experiment: {
          ...state.experiment,
          fork: action.fork,
        },
      }
    }
    case 'SAVE_PARTE': {
      return {
        ...state,
        experiment: {
          ...state.experiment,
          [action.parte]: {
            values: action.values,
            time: action.time,
          },
        },
      }
    }
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
        user: {
          ...state.user!,
          allowsNews: action.allowsNews,
        },
      }
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData,
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData)

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
